import { useEffect, useRef } from 'react'

import { segmentApi } from '@edulastic/api'

import { hashForSegment } from '../common/components/AISummary/utils'
import { isReportPath, SAR_REPORT_PATHS } from '../common/constants/reportPaths'
import { reportPearyContextIdSelector } from '../common/ducks/reportPearyContext'

/**
 * PCI-6 / SAR-only host bridge between Pear Assessment reports and the Peary embed.
 *
 * Scope: Single Assessment Report (SAR) routes only — normal Peary chat elsewhere is
 * unchanged. Question chips are fetched on explicit user action (Deep Dive, New Chat),
 * not when the AI summary first loads.
 */

// Peary is a single global widget, so whether report mode is currently applied to it, and
// the snapshot it was last stamped with, are module-level state rather than per hook
// instance: SAR tab switches remount AISummary while Peary keeps its config.
let isReportModeApplied = false
const EMPTY_STAMPED_CONTEXT = {
  contextId: null,
  filterFingerprint: null,
  reportType: null,
  pathname: null,
}
let lastStampedContext = EMPTY_STAMPED_CONTEXT

/** Composer hint while report context mode is active (chip-only, no free-form input). */
export const REPORT_COMPOSER_PLACEHOLDER =
  'Select a suggested question above to explore this report.'

/** SAR routes that currently participate in Peary Report Context. */
export const isSARReportRoute = (pathname = '') =>
  isReportPath(pathname, SAR_REPORT_PATHS)

/**
 * Whether the user is on SAR with an active report snapshot in Redux.
 * Used by App.js to skip generic product preprompts on New Chat when report chips apply.
 */
export const isActiveSARReportPearyContext = () => {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  if (!isSARReportRoute(pathname)) return false
  const store =
    typeof window !== 'undefined' && typeof window.getStore === 'function'
      ? window.getStore()
      : null
  if (!store) return false
  return Boolean(reportPearyContextIdSelector(store.getState()))
}

/**
 * Re-stamp report config and (for Deep Dive) open the drawer.
 *
 * The chip fetch itself is owned by the Peary iframe, which fires
 * `__report_context_init__` once the drawer is open. Sending it from here too
 * produced duplicate Report AI calls and skipped the iframe's loading state,
 * because host-driven sends bypass the iframe and never set `isLoading`.
 */
export const bootstrapReportPearyChips = ({
  contextId,
  reportType,
  filterFingerprint,
  productUserId,
  openDrawer = false,
}) => {
  if (
    !contextId ||
    !window.pear?.chat?.updateConfig ||
    !isSARReportRoute(window.location.pathname)
  ) {
    return
  }

  window.pear.chat.updateConfig({
    reportContextMode: true,
    reportContextLabel: '[Report Context]',
    composerDisabled: true,
    composerPlaceholder: REPORT_COMPOSER_PLACEHOLDER,
    contextId,
    reportType,
    filterFingerprint,
    productUserId: productUserId ? String(productUserId) : undefined,
    preprompts: [],
    reportContextStatus: undefined,
  })
  isReportModeApplied = true
  if (openDrawer) {
    window.pear?.chat?.open()
  }
}

/**
 * Put Peary back into normal chat mode and clear the Redux report context.
 * No-op when report mode is not applied, so every exit path can call it safely.
 */
const exitReportPearyMode = ({ clearReportPearyContext, staleEvent }) => {
  if (!isReportModeApplied) return
  isReportModeApplied = false
  lastStampedContext = EMPTY_STAMPED_CONTEXT
  if (staleEvent) {
    segmentApi.genericEventTrack('PearyReport', {
      action: 'ContextStale',
      ...staleEvent,
    })
  }
  clearReportPearyContext?.()
  window.pear?.chat?.updateConfig?.({
    reportContextMode: false,
    reportContextLabel: undefined,
    composerDisabled: false,
    composerPlaceholder: undefined,
    contextId: undefined,
    reportType: undefined,
    filterFingerprint: undefined,
    contextStaleReason: undefined,
    productUserId: undefined,
    // Restore App.js's product preprompts (report mode only blanked them via updateConfig,
    // never in PEAR_CHAT_CONFIG). `undefined` would leave normal Peary with no chips.
    preprompts: window.PEAR_CHAT_CONFIG?.preprompts,
    reportContextStatus: undefined,
  })
}

/** Test-only: reset module state between specs. */
export const resetReportPearyModeForTests = () => {
  isReportModeApplied = false
  lastStampedContext = EMPTY_STAMPED_CONTEXT
}

const PEARY_NEW_CHAT_EVENTS = new Set([
  'peary-start-new-session', // header "New Chat" + stale-banner "Start New Chat"
  'peary-restart-new-session', // long-conversation "Restart"
])

/**
 * SAR host bridge for Peary Report Context (PCI-6 / LLD Feature #4).
 *
 * Effect 1 — Context sync: stamp report mode when AI summary context is available;
 *            clear on unmount or when leaving SAR. Does not fetch question chips.
 * Effect 2 — New Chat bootstrap: after Peary resets the session, refetch report chips.
 * Effect 4 — Unmount after leaving SAR: exit report mode even if no context was active.
 * Effect 5 — Summary refetching / failed on SAR: tell Peary the old context is stale and
 *            no new one exists yet, so old chips cannot answer for the new filters.
 */
export const useReportPearyBridge = ({
  isPearyChatEnabled = false,
  contextId,
  reportType,
  filterFingerprint,
  productUserId,
  summaryPending = false,
  setReportPearyContext,
  clearReportPearyContext,
}) => {
  const bootstrapTimerRef = useRef(null)
  // Chips currently shown in Peary, sent with ChipClick as `otherChipText`.
  const shownChipsRef = useRef([])

  // --- Effect 1: stamp / clear report context (passive; no chip fetch) ---
  useEffect(() => {
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : ''
    const onSar = isSARReportRoute(pathname)

    if (!isPearyChatEnabled || !contextId || !onSar) {
      return undefined
    }

    const prev = lastStampedContext
    let contextStaleReason
    if (prev.contextId && prev.contextId !== contextId) {
      // Same reportType + new contextId → filters changed.
      // reportType also changed → report/page navigation.
      const reportTypeChanged =
        !!prev.reportType && !!reportType && prev.reportType !== reportType
      contextStaleReason = reportTypeChanged ? 'report_change' : 'filter_change'

      segmentApi.genericEventTrack('PearyReport', {
        action: 'ContextStale',
        reportType,
        contextIdHash: hashForSegment(contextId),
        reason: contextStaleReason,
        pagePath: pathname,
      })
    }

    setReportPearyContext?.({
      contextId,
      reportType,
      filterFingerprint,
      updatedAt: Date.now(),
    })

    window.pear?.chat?.updateConfig?.({
      reportContextMode: true,
      reportContextLabel: '[Report Context]',
      composerDisabled: true,
      composerPlaceholder: REPORT_COMPOSER_PLACEHOLDER,
      contextId,
      reportType,
      filterFingerprint,
      contextStaleReason,
      productUserId: productUserId ? String(productUserId) : undefined,
      preprompts: [],
      reportContextStatus: undefined,
    })
    isReportModeApplied = true

    // Advance only when contextId changes so prev.reportType stays paired with the
    // snapshot we last classified against (fingerprint can update one render earlier).
    if (prev.contextId !== contextId) {
      lastStampedContext = {
        contextId,
        filterFingerprint,
        reportType,
        pathname,
      }
    }

    return () => {
      // This cleanup also runs before every re-run (filter / contextId change), not just
      // on unmount. Tearing report mode down there would post `reportContextMode: false`
      // to the iframe first, wiping the session binding it needs to recognise the next
      // contextId as stale — so the banner never appeared. Only tear down when the user
      // has actually left SAR; the effect body re-stamps the new context otherwise.
      if (isSARReportRoute(window.location.pathname)) {
        return
      }
      exitReportPearyMode({
        clearReportPearyContext,
        staleEvent: {
          reportType,
          contextIdHash: hashForSegment(contextId),
          reason: 'leave_report',
          pagePath: window.location.pathname,
        },
      })
    }
  }, [
    isPearyChatEnabled,
    contextId,
    reportType,
    filterFingerprint,
    productUserId,
    setReportPearyContext,
    clearReportPearyContext,
  ])

  // --- Effect 5: summary refetching / failed while report mode is on ---
  // Without this Peary keeps the previous contextId: no stale banner, and its chips keep
  // answering for the old filters. Clearing contextId also stops chip bootstrap / Retry
  // from running against the old snapshot until a new summary arrives (Effect 1).
  useEffect(() => {
    if (!isPearyChatEnabled || contextId || !isReportModeApplied) return
    if (!isSARReportRoute(window.location.pathname)) return

    const prev = lastStampedContext
    const reportTypeChanged =
      !!prev.reportType && !!reportType && prev.reportType !== reportType
    window.pear?.chat?.updateConfig?.({
      reportContextMode: true,
      reportContextLabel: '[Report Context]',
      composerDisabled: true,
      composerPlaceholder: REPORT_COMPOSER_PLACEHOLDER,
      contextId: undefined,
      reportType,
      filterFingerprint,
      contextStaleReason: reportTypeChanged ? 'report_change' : 'filter_change',
      preprompts: [],
      reportContextStatus: summaryPending ? 'loading' : 'unavailable',
    })
  }, [
    isPearyChatEnabled,
    contextId,
    summaryPending,
    reportType,
    filterFingerprint,
  ])

  // --- Effect 4: leaving SAR while no context is active (e.g. after a failed fetch) ---
  // On SAR a failed summary intentionally keeps report mode (Peary shows its Retry
  // state). Effect 1's cleanup only exists while a contextId is stamped, so without
  // this, leaving SAR after a failure would keep Peary stuck in report mode.
  useEffect(
    () => () => {
      if (isSARReportRoute(window.location.pathname)) return
      // Report the snapshot Peary was last stamped with — that is what goes stale here.
      const lastStamped = lastStampedContext
      exitReportPearyMode({
        clearReportPearyContext,
        staleEvent: {
          reportType: lastStamped.reportType,
          contextIdHash: hashForSegment(lastStamped.contextId),
          reason: 'leave_report',
          pagePath: window.location.pathname,
        },
      })
    },
    [clearReportPearyContext]
  )

  // --- Effect 3: report funnel events relayed from the Peary iframe (LLD Feature #5) ---
  // `contextId` is not sent with these events.
  // Also kept while report mode stays on without a context (summary refetching / failed),
  // so e.g. New Chat in that state is still tracked as StartNewChat.
  useEffect(() => {
    if (!isPearyChatEnabled || (!contextId && !isReportModeApplied)) {
      return undefined
    }
    if (!isSARReportRoute(window.location.pathname)) return undefined

    const track = (action, props) =>
      segmentApi.genericEventTrack('PearyReport', {
        action,
        reportType,
        ...props,
      })

    const onPearyMessage = (event) => {
      const data = event.data
      if (!data || typeof data !== 'object') return
      switch (data.type) {
        case 'peary-report-chips-shown':
          shownChipsRef.current = Array.isArray(data.chips) ? data.chips : []
          break
        case 'peary-preprompt-click':
          if (data.reportContextMode && data.chipIndex >= 0) {
            const chipText = String(data.preprompt ?? '').trim()
            track('ChipClick', {
              chipIndex: data.chipIndex,
              chipText,
              otherChipText: shownChipsRef.current?.join(';'),
            })
          }
          break
        case 'peary-report-answer':
          if (data.status === 'success') {
            track('AnswerSuccess', { latencyMs: data.latencyMs })
          } else if (data.status === 'error') {
            track('AnswerError', {
              errorCode: data.errorCode || 'unknown',
              latencyMs: data.latencyMs,
            })
          }
          break
        case 'peary-start-new-session':
        case 'peary-restart-new-session':
          track('StartNewChat', { trigger: data.trigger || 'header' })
          break
        default:
      }
    }

    window.addEventListener('message', onPearyMessage)
    return () => window.removeEventListener('message', onPearyMessage)
  }, [isPearyChatEnabled, contextId, reportType])

  // --- Effect 2: refetch report chips after Peary New Chat / Restart ---
  useEffect(() => {
    if (!isPearyChatEnabled || !contextId) return undefined
    if (!isSARReportRoute(window.location.pathname)) return undefined

    const onHostMessage = (event) => {
      const type = event.data?.type
      if (!PEARY_NEW_CHAT_EVENTS.has(type)) return
      if (!isSARReportRoute(window.location.pathname)) return

      if (bootstrapTimerRef.current) {
        window.clearTimeout(bootstrapTimerRef.current)
      }
      // Wait for the pear script session reset, then re-assert report config so
      // the iframe still has contextId when it refetches chips for the new session.
      bootstrapTimerRef.current = window.setTimeout(() => {
        bootstrapTimerRef.current = null
        if (!isSARReportRoute(window.location.pathname)) return
        bootstrapReportPearyChips({
          contextId,
          reportType,
          filterFingerprint,
          productUserId,
        })
      }, 150)
    }

    window.addEventListener('message', onHostMessage)
    return () => {
      window.removeEventListener('message', onHostMessage)
      if (bootstrapTimerRef.current) {
        window.clearTimeout(bootstrapTimerRef.current)
        bootstrapTimerRef.current = null
      }
    }
  }, [
    isPearyChatEnabled,
    contextId,
    reportType,
    filterFingerprint,
    productUserId,
  ])
}
